import { distanceBetweenLatLng } from "../utils/geolocation/distance-between-latlng.js";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { onBrowserVisibilityChange } from "@therms/web-js";
import { proxy, useSnapshot } from "valtio";
//#region src/hooks/useGeolocation.ts
var defaultOptions = {
	enableHighAccuracy: true,
	maximumAge: 1e4,
	timeout: 3e4
};
var globalGeolocationState = proxy({
	error: void 0,
	loading: false,
	previousSensorState: void 0,
	sensorState: {
		accuracy: void 0,
		altitude: void 0,
		altitudeAccuracy: void 0,
		heading: void 0,
		lat: void 0,
		latitude: void 0,
		lng: void 0,
		longitude: void 0,
		speed: void 0,
		timestamp: void 0
	},
	watchId: void 0
});
/**
* The logic in this hook mounts when the package is loaded in the browser so we don't want to start asking for location
* permissions until this component has actually been mounted.
*/
var serviceStarted = false;
var getCurrentPosition = (opts = {}) => {
	if (globalGeolocationState.loading) return;
	const onEvent = (event) => {
		if (!globalGeolocationState.loading && globalGeolocationState.sensorState.timestamp) {
			const timestampIsFresh = new Date(globalGeolocationState.sensorState.timestamp).getTime() >= new Date(event.timestamp).getTime() - 2e4;
			const distanceIsShort = distanceBetweenLatLng(globalGeolocationState.sensorState.lat, globalGeolocationState.sensorState.lng, event.coords.latitude, event.coords.longitude) <= 5;
			if (timestampIsFresh && distanceIsShort) return;
		}
		globalGeolocationState.error = void 0;
		globalGeolocationState.loading = false;
		if (globalGeolocationState.sensorState.timestamp) globalGeolocationState.previousSensorState = globalGeolocationState.sensorState;
		globalGeolocationState.sensorState = {
			accuracy: +event.coords.accuracy.toFixed(0),
			altitude: event.coords.altitude || 0,
			altitudeAccuracy: event.coords.altitudeAccuracy || 0,
			heading: event.coords.heading || 0,
			lat: event.coords.latitude,
			latitude: event.coords.latitude,
			lng: event.coords.longitude,
			longitude: event.coords.longitude,
			speed: event.coords.speed || 0,
			timestamp: new Date(event.timestamp).toISOString()
		};
	};
	const onEventError = (err) => {
		if (err) {
			console.error(`navigator.geolocation error`, err);
			globalGeolocationState.sensorState = {
				accuracy: void 0,
				altitude: void 0,
				altitudeAccuracy: void 0,
				heading: void 0,
				lat: void 0,
				latitude: void 0,
				lng: void 0,
				longitude: void 0,
				speed: void 0,
				timestamp: void 0
			};
			globalGeolocationState.loading = false;
			try {
				navigator.geolocation.clearWatch(globalGeolocationState.watchId);
			} catch (e) {
				console.warn("navigator.geolocation.clearWatch() error", e.message);
			}
			globalGeolocationState.watchId = void 0;
			globalGeolocationState.error = {
				code: err.code || 0,
				message: err.message || "System error while fetching GPS",
				PERMISSION_DENIED: err.code === 1,
				POSITION_UNAVAILABLE: err.code === 2,
				TIMEOUT: err.code === 3
			};
		}
	};
	if (!globalGeolocationState.watchId) {
		globalGeolocationState.loading = true;
		globalGeolocationState.watchId = navigator.geolocation.watchPosition(onEvent, onEventError, defaultOptions);
	} else if (opts.force) {
		globalGeolocationState.loading = true;
		const wasWatching = typeof globalGeolocationState.watchId !== "undefined";
		if (wasWatching) {
			try {
				navigator.geolocation.clearWatch(globalGeolocationState.watchId);
			} catch (e) {
				console.warn("navigator.geolocation.clearWatch() error", e.message);
			}
			globalGeolocationState.watchId = void 0;
		}
		navigator.geolocation.getCurrentPosition((event) => {
			onEvent(event);
			if (wasWatching) globalGeolocationState.watchId = navigator.geolocation.watchPosition(onEvent, onEventError, defaultOptions);
		}, onEventError, {
			...defaultOptions,
			...opts
		});
	}
};
onBrowserVisibilityChange((visible) => {
	if (!serviceStarted) return;
	if (!visible) {
		if (typeof globalGeolocationState.watchId !== "undefined") {
			try {
				navigator.geolocation.clearWatch(globalGeolocationState.watchId);
			} catch (e) {
				console.warn("navigator.geolocation.clearWatch() error", e.message);
			}
			globalGeolocationState.watchId = void 0;
		}
	} else getCurrentPosition();
});
/**
* This hook will only attempt to get the geolocation once and not update again unless refresh() is called
*/
function useGeolocation() {
	serviceStarted = true;
	const snapshot = useSnapshot(globalGeolocationState);
	const [frozenSnapshot, setFrozenSnapshot] = useState(() => {
		if (snapshot.sensorState && dayjs(snapshot.sensorState.timestamp).isBefore(dayjs().subtract(20, "seconds"))) return snapshot;
	});
	useEffect(() => {
		getCurrentPosition();
	}, []);
	useEffect(() => {
		if (!frozenSnapshot) setFrozenSnapshot(snapshot);
	}, [snapshot]);
	const refresh = useCallback(() => {
		getCurrentPosition({
			enableHighAccuracy: true,
			force: true,
			maximumAge: 5e3,
			timeout: 2e4
		});
	}, []);
	useEffect(() => {
		if (snapshot.sensorState.timestamp) {
			const staleTime = dayjs(snapshot.sensorState.timestamp).add(5, "minute");
			if (dayjs().isAfter(staleTime)) refresh();
			else {
				const interval = setInterval(() => {
					if (dayjs().isAfter(staleTime)) {
						refresh();
						clearInterval(interval);
					}
				}, 6e4);
				return () => clearInterval(interval);
			}
		}
	}, [snapshot.sensorState.timestamp]);
	return {
		...snapshot.sensorState,
		error: globalGeolocationState.error,
		loading: globalGeolocationState.loading,
		previous: snapshot.previousSensorState,
		refresh
	};
}
/**
* This hook will continuously update the geolocation when it changes.
*/
function useWatchGeolocation() {
	const snapshot = useSnapshot(globalGeolocationState);
	useEffect(() => {
		getCurrentPosition();
	}, []);
	const refresh = useCallback(() => {
		getCurrentPosition({ force: true });
	}, []);
	useEffect(() => {
		if (snapshot.sensorState.timestamp) {
			const staleTime = dayjs(snapshot.sensorState.timestamp).add(5, "minute");
			if (dayjs().isAfter(staleTime)) refresh();
			else {
				const interval = setInterval(() => {
					if (dayjs().isAfter(staleTime)) {
						refresh();
						clearInterval(interval);
					}
				}, 6e4);
				return () => clearInterval(interval);
			}
		}
	}, [snapshot.sensorState.timestamp]);
	return {
		...snapshot.sensorState,
		error: globalGeolocationState.error,
		loading: globalGeolocationState.loading,
		previous: snapshot.previousSensorState,
		refresh
	};
}
//#endregion
export { useGeolocation, useWatchGeolocation };

//# sourceMappingURL=useGeolocation.js.map